import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import clsx from 'clsx';
import { Alert, Button, Modal } from '@/components/ui';

/**
 * Shows generated credentials exactly once.
 *
 * The password is never persisted in plaintext — it exists in this dialog and
 * nowhere else — so the copy affordances and the warning are load-bearing, not
 * decoration.
 */
export function CredentialsDialog({
  email,
  password,
  fullName,
  onClose,
}: {
  email: string;
  password: string;
  fullName: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard is unavailable outside a secure context. The value is on
      // screen and selectable, so there is nothing to recover from.
    }
  };

  const rows = [
    { label: 'Login ID (email)', value: email, className: 'text-brand-300' },
    { label: 'Password', value: password, className: 'text-slate-100' },
  ];

  return (
    <Modal
      open
      onClose={onClose}
      title="Credentials generated"
      description={`Share these with ${fullName} through a channel you trust.`}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => void copy('both', `Login: ${email}\nPassword: ${password}`)}
            icon={<Copy className="h-4 w-4" />}
          >
            {copied === 'both' ? 'Copied' : 'Copy both'}
          </Button>
          <Button onClick={onClose}>Done</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert severity="warning" title="This password is shown once">
          It is stored only as a hash and cannot be retrieved later. If it is lost you will have to reset it.
        </Alert>

        {rows.map((row) => (
          <div key={row.label} className="rounded-lg border border-surface-border bg-surface-base p-4">
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-slate-500">{row.label}</p>
            <div className="flex items-center gap-3">
              <code className={clsx('flex-1 break-all font-mono text-lg font-semibold', row.className)}>
                {row.value}
              </code>
              <button
                onClick={() => void copy(row.label, row.value)}
                className="shrink-0 rounded-lg p-2 text-slate-500 transition hover:bg-surface-hover hover:text-slate-200"
                aria-label={`Copy ${row.label}`}
              >
                {copied === row.label ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
